import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../OtherCalculator/CountriesMainCalculatorsList/SAMainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة النقاط بمسار";
const DESCRIPTION =
  "احسب نقاطك في نظام مسار لوزارة التعليم السعودية. أدخل نتائجك في تقييم الأداء والمبادرات والإنجازات والتطوير والتدريب واحصل على نقاطك الكلية وخيارات التحسين فوراً.";
const KEYWORDS =
  "حاسبة نقاط مسار, نظام مسار وزارة التعليم, نقاط الترقية مسار, تقييم الأداء مسار, المبادرات والإنجازات مسار, التطوير والتدريب مسار, حاسبة مسار السعودية";
const OG_URL =
  "https://do-calculate.com/calculator/ar/countries/SA/calculators-list/massar-points-calculator/";

// Training point values
const DARB_PTS = 1;       // دروب / أُثرائي
const IPA_SHORT_PTS = 5;  // دورة قصيرة من معهد الإدارة
const IPA_EXAM_PTS = 10;  // دورة منتهية بالاجتياز من معهد الإدارة

function getTrainingOptions(gap) {
  if (gap <= 0) return [];
  const options = [];

  // Try all combinations of IPA_EXAM + IPA_SHORT + DARB that fill the gap
  const maxExam = Math.ceil(gap / IPA_EXAM_PTS);
  for (let e = 0; e <= maxExam; e++) {
    const rem1 = gap - e * IPA_EXAM_PTS;
    if (rem1 < 0) break;
    const maxShort = Math.ceil(rem1 / IPA_SHORT_PTS);
    for (let s = 0; s <= maxShort; s++) {
      const rem2 = rem1 - s * IPA_SHORT_PTS;
      if (rem2 < 0) break;
      // fill the rest with Darb
      const d = rem2 > 0 ? Math.ceil(rem2 / DARB_PTS) : 0;
      if (options.length < 5) {
        options.push({ exam: e, short: s, darb: d });
      }
    }
  }

  // Deduplicate and filter
  const seen = new Set();
  return options.filter((o) => {
    const key = `${o.exam}-${o.short}-${o.darb}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildOptionText(opt) {
  const parts = [];
  if (opt.exam > 0)
    parts.push(`${opt.exam} منتهية بالاجتياز من معهد الإدارة`);
  if (opt.short > 0)
    parts.push(`${opt.short} دورة قصيرة من معهد الإدارة`);
  if (opt.darb > 0)
    parts.push(`${opt.darb} دورة دروب أو أُثرائي`);
  return parts.join(" و ");
}

function MassarPointsCalculator() {
  const [performanceScore, setPerformanceScore] = useState("");
  const [initiativesScore, setInitiativesScore] = useState("");
  const [trainingScore, setTrainingScore] = useState("");
  const [result, setResult] = useState(null);

  const handleReset = () => {
    setPerformanceScore("");
    setInitiativesScore("");
    setTrainingScore("");
  };

  useEffect(() => {
    const p = parseFloat(performanceScore);
    const i = parseFloat(initiativesScore);
    const t = parseFloat(trainingScore);

    if (
      performanceScore === "" ||
      initiativesScore === "" ||
      trainingScore === "" ||
      isNaN(p) || isNaN(i) || isNaN(t) ||
      p < 0 || p > 100 ||
      i < 0 || i > 100 ||
      t < 0 || t > 100
    ) {
      setResult(null);
      return;
    }

    // Performance: 30 pts — binary: only 100 gives full 30
    const perfPts = parseFloat(((p / 100) * 30).toFixed(2));
    const perfFull = p === 100;

    // Initiatives & Achievements: 30 pts
    const initPts = parseFloat(((i / 100) * 30).toFixed(2));
    const initGapPts = parseFloat((30 - initPts).toFixed(2));
    // Each volunteer hour = 1 point -> gap in hours
    const initHoursNeeded = Math.ceil(initGapPts);

    // Training & Development: 40 pts
    const trainPts = parseFloat(((t / 100) * 40).toFixed(2));
    const trainGapPts = parseFloat((40 - trainPts).toFixed(2));
    const trainingOptions = getTrainingOptions(Math.ceil(trainGapPts));

    const total = parseFloat((perfPts + initPts + trainPts).toFixed(2));

    setResult({
      perfPts, perfFull,
      initPts, initGapPts, initHoursNeeded,
      trainPts, trainGapPts, trainingOptions,
      total,
    });
  }, [performanceScore, initiativesScore, trainingScore]);

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة النقاط بمسار</strong> تساعدك على معرفة نقاطك الكلية في نظام مسار
                لوزارة التعليم السعودية، وخيارات لتحسينها. أدخل نتائجك في المحاور الثلاثة
                وستظهر النتيجة تلقائياً. الحاسبة خاصة بموظفي وزارة التعليم فقط.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  ضع نتيجة المرشح التي حصلت عليها في مسار
                </h2>
                <p style={{ fontSize: "13px", margin: "4px 0 0" }}>
                  (خاص بموظفي وزارة التعليم فقط)
                </p>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6 mb-3">
                  <label htmlFor="performanceScore">تقييم الأداء (من 100):</label>
                  <input
                    id="performanceScore"
                    type="number"
                    className="form-control"
                    value={performanceScore}
                    onChange={(e) => setPerformanceScore(e.target.value)}
                    placeholder="مثال: 70"
                    min="0"
                    max="100"
                    style={{ textAlign: "center", fontSize: "18px" }}
                  />
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  <label htmlFor="initiativesScore">المبادرات والإنجازات (من 100):</label>
                  <input
                    id="initiativesScore"
                    type="number"
                    className="form-control"
                    value={initiativesScore}
                    onChange={(e) => setInitiativesScore(e.target.value)}
                    placeholder="مثال: 70"
                    min="0"
                    max="100"
                    style={{ textAlign: "center", fontSize: "18px" }}
                  />
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  <label htmlFor="trainingScore">التطوير والتدريب (من 100):</label>
                  <input
                    id="trainingScore"
                    type="number"
                    className="form-control"
                    value={trainingScore}
                    onChange={(e) => setTrainingScore(e.target.value)}
                    placeholder="مثال: 70"
                    min="0"
                    max="100"
                    style={{ textAlign: "center", fontSize: "18px" }}
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-2">
                <button
                  onClick={handleReset}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  امسح
                </button>
              </div>

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>
                      النتيجة الكلية: {result.total} / 100 نقطة
                    </strong>
                  </h3>

                  <table className="table bmi-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th className="bmi-header" style={{ textAlign: "center" }}>المعيار</th>
                        <th className="bmi-header" style={{ textAlign: "center" }}>النتيجة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Performance row */}
                      <tr>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>تقييم الأداء</td>
                        <td style={{ textAlign: "center" }}>
                          {result.perfFull ? (
                            <span style={{ color: "#155724" }}>
                              ✅ حصلت على كامل النقاط ({result.perfPts} / 30)
                            </span>
                          ) : (
                            <span style={{ color: "#721c24" }}>
                              {result.perfPts} / 30 — للأسف، في حال كان الأداء ناقصاً لا يمكن
                              زيادة النقاط إلا بالحصول على تقييم كامل العام القادم.
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Initiatives row */}
                      <tr>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          المبادرات<br />والإنجازات
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {result.initGapPts <= 0 ? (
                            <span style={{ color: "#155724" }}>
                              ✅ حصلت على كامل النقاط (30 / 30)
                            </span>
                          ) : (
                            <span>
                              {result.initPts} / 30 — تحتاج إلى{" "}
                              <strong>{result.initHoursNeeded} ساعة تطوع</strong> لإكمال النقاط.
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Training row */}
                      <tr>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          التطوير<br />والتدريب
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {result.trainGapPts <= 0 ? (
                            <span style={{ color: "#155724" }}>
                              ✅ حصلت على كامل النقاط (40 / 40)
                            </span>
                          ) : (
                            <span>
                              حصلت على <strong>{result.trainPts} نقطة</strong> من أصل 40،
                              تحتاج إلى <strong>{Math.ceil(result.trainGapPts)} نقطة إضافية</strong>.
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Training options row */}
                      {result.trainGapPts > 0 && result.trainingOptions.length > 0 && (
                        <tr>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>خيارات التدريب</td>
                          <td>
                            {result.trainingOptions.map((opt, idx) => (
                              <div key={idx} style={{ marginBottom: "6px" }}>
                                {idx > 0 && (
                                  <span style={{ color: "#888", display: "block", margin: "4px 0" }}>
                                    أو
                                  </span>
                                )}
                                يمكنك أخذ <strong>{buildOptionText(opt)}</strong>
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">

            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هو نظام مسار؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  مسار هو نظام إدارة الأداء الوظيفي لوزارة التعليم السعودية. يُقيَّم الموظف
                  سنوياً عبر ثلاثة محاور رئيسية تُجمع نتائجها لتحديد نقاطه الكلية التي تُستخدم
                  في قرارات الترقية والحوافز.
                </p>
                <table className="table bmi-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المحور</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>الوزن</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>آلية التحسين</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>تقييم الأداء</td>
                      <td style={{ textAlign: "center" }}>30 نقطة</td>
                      <td style={{ textAlign: "center" }}>الحصول على تقييم كامل العام القادم</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>المبادرات والإنجازات</td>
                      <td style={{ textAlign: "center" }}>30 نقطة</td>
                      <td style={{ textAlign: "center" }}>ساعات التطوع (1 ساعة = 1 نقطة)</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>التطوير والتدريب</td>
                      <td style={{ textAlign: "center" }}>40 نقطة</td>
                      <td style={{ textAlign: "center" }}>دورات دروب / معهد الإدارة</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>كيف تُحسب نقاط التدريب؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  نقاط محور التطوير والتدريب تُحسب بضرب نتيجتك في مسار × 40%. لتحسين نقاطك يمكنك
                  إكمال الدورات التالية:
                </p>
                <table className="table bmi-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>نوع الدورة</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>النقاط</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>دورة دروب أو أُثرائي</td>
                      <td style={{ textAlign: "center" }}>1 نقطة</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>دورة قصيرة من معهد الإدارة</td>
                      <td style={{ textAlign: "center" }}>5 نقاط</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>دورة منتهية بالاجتياز من معهد الإدارة</td>
                      <td style={{ textAlign: "center" }}>10 نقاط</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>تنبيهات مهمة</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "2" }}>
                  <li>
                    هذه الحاسبة <strong>خاصة بموظفي وزارة التعليم</strong> فقط ممن يستخدمون
                    نظام مسار.
                  </li>
                  <li>
                    نقاط الأداء لا يمكن تحسينها إلا بالحصول على تقييم 100 في العام القادم.
                  </li>
                  <li>
                    الحد الأقصى لنقاط التطوير والتدريب هو 40 نقطة ولا تتجاوزها حتى لو أكملت
                    دورات أكثر.
                  </li>
                  <li>
                    الأرقام الظاهرة تُقرَّب للأعلى لضمان تغطية النقاط المطلوبة.
                  </li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ fontSize: "14px" }}>
                  ⚠️ هذه الحاسبة للاستخدام الإرشادي. يرجى مراجعة نظام مسار الرسمي
                  للتأكد من أحدث الأنظمة والمتطلبات.
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default MassarPointsCalculator;
